import { ServiceStack } from '@zerops/models/service-stack';
import { Location } from '@zerops/models/settings';

export type ProjectStatus = 'NEW' | 'CREATING' | 'ACTIVE' | 'DELETING' | 'STOPPING' | 'STARTING' | 'DELETED' | 'FAILED' | 'STOPPED';

export type ProjectCoreMode = 'LIGHT' | 'SERIOUS' | 'LEGACY';

// The full enum, not a subset — per InDtoBodyClientUserProjectRole the API accepts
// BASIC_USER and NO_ACCESS overrides too, though the dashboard only assigns two.
export type ProjectRole = 'OWNER' | 'ADMIN' | 'BASIC_USER' | 'READ_ONLY' | 'NO_ACCESS';

export enum ProjectRoles {
  Owner = 'OWNER',
  Admin = 'ADMIN',
  BasicUser = 'BASIC_USER',
  ReadOnly = 'READ_ONLY',
  NoAccess = 'NO_ACCESS'
}

// The subset the dashboard lets a user pick when granting project access.
export type AssignableProjectRole = Extract<ProjectRole, 'ADMIN' | 'READ_ONLY'>;

export interface ProjectUserRole {
  id: string;
  roleCode: ProjectRole;
}

export interface ProjectUserRoleApiResponse {
  id: string;
  clientId: string;
  clientUserId: string;
  projectId: string;
  roleCode: ProjectRole;
  created: string;
  lastUpdate: string;
}

export enum ProjectStatuses {
  New = 'NEW',
  Creating = 'CREATING',
  Active = 'ACTIVE',
  Deleting = 'DELETING',
  Stopping = 'STOPPING',
  Starting = 'STARTING',
  Deleted = 'DELETED',
  Failed = 'FAILED',
  Stopped = 'STOPPED'
}

export enum ProjectEnvTypes {
  System = 'SYSTEM',
  User = 'USER'
}

export interface ProjectEnv {
  clientId: string;
  content: string;
  created: string;
  id: string;
  key: string;
  lastUpdate: string;
  projectId: string;
  editable?: boolean;
  sensitive?: boolean;
  type: ProjectEnvTypes;
}

export class Project {
  id: string;
  clientId: string;
  name: string;
  description: string;
  envList: ProjectEnv[];
  tagList: string[];
  status: ProjectStatuses;
  created: string;
  lastUpdate: string;
  logForwardingType: string;
  publicIpV4: string;
  sharedIpV4: string;
  publicIpV4PendingRequest: boolean;
  publicIpV4RequestDisabledTill: string;
  publicIpV6: string;
  maxCreditLimit: number;
  mode: ProjectCoreMode;
  publicZone: string;
  publicIpV4Shared: boolean;
  recipeSource: string | null;
  recipeSourceUrl: string | null;
  userRoles?: ProjectUserRoleApiResponse[];
  // null when the project creation failed and the rollback removed its instance (project stays in NEW)
  primaryInstanceLocation?: Location | null;
}

export interface ProjectAddPayload extends Omit<Partial<Project>, 'userRoles'> {
  userRoles?: ProjectUserRole[];
}

export interface ProjectUpdatePayload extends Omit<Partial<Project>, 'userRoles'> {
  userRoles?: {
    userRoles: ProjectUserRole[];
  };
}

export interface L7Config {
  name: string;
  current: string | null;
  default: string;
}

export interface L7ConfigSubmitValue {
  name: string;
  value: string | null;
}

export interface L7ConfigResponse {
  values: L7Config[];
}

export interface L7ConfigPayload {
  values: L7ConfigSubmitValue[];
}

export class ProjectWithServiceStacks extends Project {
  serviceStacks: ServiceStack[];
}

export class Log {
  expiry: string;
  signature: string;
  url: string;
  urlInfo: string;
  urlPlain: string;
  urlUi: string;
}

export class LogData {
  id: string;
  appName: string;
  client: string;
  content: string;
  facility: number;
  facilityLabel: string;
  hostname: string;
  msgId: string;
  priority: number;
  procId: string;
  severity: number;
  severityLabel: string;
  structuredData: string;
  tag: string;
  timestamp: string;
  tlsPeer: string;
  version: number;
  _ws?: boolean;
}

export const PROJECT_CREATION_FAILED_LOCATION_LABEL = 'Creation failed';

// missing `primaryInstanceLocation` means unknown, only explicit null means the project has no instance,
// such a project can only be deleted, every other action fails
export const isProjectWithoutInstance = (project?: Pick<Project, 'primaryInstanceLocation'>) =>
  project?.primaryInstanceLocation === null;

export const canRequestIpV4 = (status?: ProjectStatuses) =>
  status === ProjectStatuses.Active || status === ProjectStatuses.Creating;

export const REQUEST_IPV4_DISABLED_TOOLTIP = 'The project must be active to request an IPv4 address.';

export const REQUEST_IPV4_UNVERIFIED_TOOLTIP = 'A dedicated IPv4 address is available after your first top-up.';

// the reason the IPv4 request is refused, undefined when it is allowed;
// the backend refuses a client without a first top-up for a paid address and for a $0 reuse alike
export const requestIpV4DisabledReason = (status?: ProjectStatuses, clientUnverified = false) => {
  if (!canRequestIpV4(status)) {
    return REQUEST_IPV4_DISABLED_TOOLTIP;
  }
  return clientUnverified
    ? REQUEST_IPV4_UNVERIFIED_TOOLTIP
    : undefined;
};

// a zero price means the addon reuses the paid period of a released IPv4 address
export const ipV4AddonButtonText = (addon?: { unitPrice: number; }) => {
  if (!addon) {
    return 'Activate unique IPv4 address';
  }
  return addon.unitPrice === 0
    ? 'Reuse paid IPv4 address'
    : `Activate for $${addon.unitPrice} per month`;
};

export const getProjectLocationLabel = (project?: Pick<Project, 'primaryInstanceLocation'>) =>
  isProjectWithoutInstance(project)
    ? PROJECT_CREATION_FAILED_LOCATION_LABEL
    : project?.primaryInstanceLocation?.name;
